import { useState } from "react"
import { FeatureFlags } from "../constants/featureFlags"
import { useFeatureFlagOverrides } from "../contexts/FeatureFlagOverrideContext"
import { Slider } from "./slider"
import "./FeatureFlagDevPanel.css"

/**
 * Development-only floating panel for toggling feature flag overrides.
 * Renders a fixed-position FAB button that opens a panel listing all registered
 * feature flags with toggle switches. Only renders in development mode.
 *
 * @returns {React.ReactElement|null} The dev panel UI, or null in non-development environments
 */
export const FeatureFlagDevPanel = () => {
  const [isOpen, setIsOpen] = useState(false)
  const { overrides, setOverride, clearAllOverrides } = useFeatureFlagOverrides()

  if (process.env.NODE_ENV !== "development") {
    return null
  }

  const handleToggle = (flagKey, isChecked) => {
    setOverride(flagKey, !isChecked)
  }

  return (
    <>
      <button
        type="button"
        data-testid="ff-dev-panel-fab"
        className="ff-dev-panel__fab"
        onClick={() => setIsOpen((previous) => !previous)}
        aria-label="Toggle feature flag dev panel"
      >
        FF
      </button>

      {isOpen && (
        <div data-testid="ff-dev-panel" className="ff-dev-panel">
          <div className="ff-dev-panel__header">
            <span className="ff-dev-panel__title">Feature Flags</span>
            <button
              type="button"
              data-testid="ff-clear-all"
              className="ff-dev-panel__clear-btn"
              onClick={clearAllOverrides}
            >
              Clear All
            </button>
          </div>
          <div className="ff-dev-panel__list">
            {Object.entries(FeatureFlags).map(([name, config]) => {
              const overrideValue = overrides[config.flagKey]
              const isChecked =
                overrideValue !== undefined ? overrideValue : config.defaultValue

              return (
                <div key={name} data-testid={`ff-flag-${name}`} className="ff-dev-panel__flag">
                  <div className="ff-dev-panel__flag-info">
                    <span className="ff-dev-panel__flag-name">{name}</span>
                    <span className="ff-dev-panel__flag-key">{config.flagKey}</span>
                  </div>
                  <div className="ff-dev-panel__flag-toggle">
                    <Slider
                      data-testid={`ff-toggle-${name}`}
                      checked={isChecked}
                      onChecked={() => handleToggle(config.flagKey, isChecked)}
                    />
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </>
  )
}
